// What makes a Jira ticket urgent, the same everywhere the Sprint tab shows one: its expedite label, a due date passed
// or within 2 days, a priority of 1 (Blocker, Highest), or down to the board's `urgentPriority` (2 adds High). The
// order puts expedite first, then the soonest due date, then the priority.

export interface UrgencyFacts { priority?: string; dueDate?: string; expedite?: boolean; done?: boolean }

const DAY = 86_400_000;

/** `1 - Blocker` → 1; Jira's names otherwise */
export function levelOf(name: string | undefined): number | undefined {
  if (!name) return undefined;
  const digit = /^\s*([1-5])\b/.exec(name);
  if (digit) return Number(digit[1]);
  const n = name.toLowerCase();
  if (/blocker|highest|critical|urgent/.test(n)) return 1;
  if (/high|major/.test(n)) return 2;
  if (/medium|normal/.test(n)) return 3;
  if (/lowest|trivial/.test(n)) return 5;
  if (/low|minor/.test(n)) return 4;
  return undefined;
}

const short = (iso: string) => new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(new Date(`${iso}T00:00:00Z`));

export interface Urgency { urgent: boolean; reasons: string[]; rank: [number, string, number] }

export function urgencyOf(f: UrgencyFacts | undefined, now: number, urgentPriority = 1): Urgency {
  const none: Urgency = { urgent: false, reasons: [], rank: [1, '9999-12-31', 3] };
  if (!f || f.done) return none;
  // 0 is the lists' `now` before their first read: the present then
  const today = new Date(now || Date.now()).toISOString().slice(0, 10);
  const parsed = f.dueDate ? Date.parse(`${f.dueDate}T00:00:00Z`) : NaN;
  const days = !Number.isNaN(parsed) ? Math.round((parsed - Date.parse(`${today}T00:00:00Z`)) / DAY) : undefined;
  const level = levelOf(f.priority);
  const soon = days !== undefined && days <= 2;
  const reasons = [
    f.expedite ? 'expedite' : '',
    days === undefined ? '' : days < 0 ? `overdue since ${short(f.dueDate!)}` : days === 0 ? 'due today' : days === 1 ? 'due tomorrow' : soon ? `due ${short(f.dueDate!)}` : '',
    level !== undefined && level <= urgentPriority ? f.priority! : '',
  ].filter(Boolean);
  // The due date compared as text: a soon one first, one within the week next (`5` sorts after an ISO year, before `9999`)
  return { urgent: reasons.length > 0, reasons, rank: [f.expedite ? 0 : 1, soon ? f.dueDate! : days !== undefined && days <= 7 ? `5${f.dueDate}` : '9999-12-31', level ?? 3] };
}

/** Smallest first: the most urgent */
export function byUrgency(a: Urgency, b: Urgency): number {
  for (let i = 0; i < a.rank.length; i++) {
    if (a.rank[i] !== b.rank[i]) return a.rank[i]! < b.rank[i]! ? -1 : 1;
  }
  return 0;
}

/** The operator's own tickets are on no board: urgent as on the widest of the server's boards (a refused one counts as 1) */
export function widestUrgentPriority(boards: Array<{ board: { id: number; urgentPriority?: number } }>): number {
  return Math.max(1, ...boards.map((b) => b.board.urgentPriority ?? 1));
}
